import React, { useEffect, useMemo, useState } from "react";
import "./IntegracaoGoogleAds.css";

const API_BASE = "/crm-wly/api";
const API_GOOGLEADS = "/crm-wly/api/googleads";

async function fetchJSON(url, options = {}) {
  const res = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.headers || {}),
      "Content-Type": "application/json",
    },
  });

  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    const msg =
      (data && data.error) ||
      (data && data.message) ||
      (typeof data === "string" ? data : "Erro na requisição");
    throw new Error(msg);
  }
  return data;
}

function readIsAdminFromLocalStorage() {
  // 1) prioridade: chave isAdmin (como no seu print)
  const raw = localStorage.getItem("isAdmin");
  if (raw !== null && raw !== undefined) {
    const v = String(raw).trim().toLowerCase();
    if (v === "true" || v === "1" || v === "yes") return true;
    if (v === "false" || v === "0" || v === "no") return false;
  }

  // 2) fallback: tenta ler objeto "usuario"
  try {
    const userRaw = localStorage.getItem("usuario");
    if (userRaw) {
      const u = JSON.parse(userRaw);
      if (u?.isAdmin === true || u?.isAdmin === 1) return true;
      if (String(u?.tipo_usuario || "").toLowerCase() === "master") return true;
      if (String(u?.tipo || "").toLowerCase() === "master") return true;
    }
  } catch (_) {}

  return false;
}

function normalizeUsersResponse(resp) {
  // seu listagem_usuarios.php retorna { usuarios: [...] }
  if (Array.isArray(resp?.usuarios)) return resp.usuarios;

  // outros formatos possíveis (mantém)
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp)) return resp;

  return [];
}


async function fetchUsuariosComFallback() {
  const candidates = [
    `/crm-wly/listagem_usuarios.php`,
  ];

  let lastErr = null;

  for (const url of candidates) {
    try {
      const r = await fetchJSON(url, { method: "GET" });
      const list = normalizeUsersResponse(r);
      if (Array.isArray(list) && list.length >= 0) return list;
    } catch (e) {
      lastErr = e;
    }
  }

  throw lastErr || new Error("Não foi possível carregar a lista de usuários (endpoint não encontrado).");
}


export default function IntegracaoGoogleAds() {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [usuarioId, setUsuarioId] = useState("");

  const [contasLoading, setContasLoading] = useState(false);
  const [contas, setContas] = useState([]);
  const [contaSearch, setContaSearch] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");

  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // 1) valida master pelo localStorage + carrega usuários
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        setMsg("");

        // regra do front: usa o que já existe no seu app (localStorage)
        const adminLS = readIsAdminFromLocalStorage();
        setIsAdmin(adminLS);

        if (!adminLS) {
          setLoading(false);
          return;
        }

        // lista usuários (NÃO é getUser.php — getUser normalmente retorna só o usuário logado)
        const list = await fetchUsuariosComFallback();
        setUsuarios(Array.isArray(list) ? list : []);
      } catch (e) {
        setErr(e?.message || "Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const usuariosFiltrados = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return usuarios;
    return usuarios.filter((u) => {
      const nome = String(u?.nome || "").toLowerCase();
      const email = String(u?.email || "").toLowerCase();
      return nome.includes(q) || email.includes(q);
    });
  }, [usuarios, userSearch]);

  const contasFiltradas = useMemo(() => {
    const q = contaSearch.trim().toLowerCase();
    if (!q) return contas;
    return contas.filter((c) => {
      const id = String(c?.customer_id || "").toLowerCase();
      const name = String(c?.customer_name || "").toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [contas, contaSearch]);

  async function carregarContasMCC() {
    try {
      setErr("");
      setMsg("");
      setContasLoading(true);

      const r = await fetchJSON(`${API_GOOGLEADS}/contas_listar.php`, { method: "GET" });
      const list = r?.data?.contas || r?.contas || [];
      setContas(Array.isArray(list) ? list : []);
      setMsg(`Contas carregadas: ${Array.isArray(list) ? list.length : 0}`);
    } catch (e) {
      setErr(e?.message || "Erro ao carregar contas");
    } finally {
      setContasLoading(false);
    }
  }

  function onSelectConta(id) {
    setCustomerId(id);
    const c = contas.find((x) => String(x?.customer_id) === String(id));
    setCustomerName(c?.customer_name || "");
  }

  async function vincular() {
    try {
      setErr("");
      setMsg("");

      if (!usuarioId) throw new Error("Selecione um usuário do Nórea.");
      if (!customerId) throw new Error("Selecione uma conta (Customer ID).");

      const payload = {
        usuario_id: Number(usuarioId),
        customer_id: String(customerId).replace(/\D+/g, ""),
        customer_name: customerName || null,
      };

      const r = await fetchJSON(`${API_GOOGLEADS}/integracoes_salvar.php`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setMsg(r?.message || "Vínculo salvo com sucesso.");
    } catch (e) {
      setErr(e?.message || "Erro ao vincular");
    }
  }

  if (loading) {
    return (
      <div className="ga-page">
        <div className="ga-card">Carregando…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="ga-page">
        <div className="ga-card">
          <h2>Integração Google Ads</h2>
          <div className="ga-alert ga-alert-ok">
            Você não é master. Esta página é somente para administradores.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ga-page">
      <div className="ga-card">
        <div className="ga-head">
          <div>
            <h2>Integração Google Ads</h2>
            <p className="ga-sub">
              Master: selecione o usuário e vincule uma conta (Customer ID) da sua MCC.
            </p>
          </div>

          <button
            type="button"
            className="ga-btn"
            onClick={carregarContasMCC}
            disabled={contasLoading}
            title="Carregar contas da MCC"
          >
            {contasLoading ? "Carregando contas..." : "Carregar contas (MCC)"}
          </button>
        </div>

        {err ? <div className="ga-alert ga-alert-err">{err}</div> : null}
        {msg ? <div className="ga-alert ga-alert-ok">{msg}</div> : null}

        <div className="ga-grid">
          {/* COLUNA ESQUERDA - Usuário */}
          <div className="ga-box">
            <h3>Usuário (Nórea)</h3>

            <label className="ga-label">Pesquisar</label>
            <input
              className="ga-input"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Nome ou e-mail..."
            />

            <label className="ga-label">Selecione um usuário</label>
            <select
              className="ga-select"
              value={usuarioId}
              onChange={(e) => setUsuarioId(e.target.value)}
            >
              <option value="">-- Selecione --</option>
              {usuariosFiltrados.map((u) => (
                <option key={u.id} value={u.id}>
                    {u.nome} {u.nome_empresa ? `(${u.nome_empresa})` : ""}
                </option>
                ))}

            </select>

            <div className="ga-hint">
              Esse usuário será o “dono” da conta Google Ads vinculada.
            </div>
          </div>

          {/* COLUNA DIREITA - Conta Google */}
          <div className="ga-box">
            <h3>Conta Google Ads (Customer)</h3>

            <label className="ga-label">Pesquisar conta</label>
            <input
              className="ga-input"
              value={contaSearch}
              onChange={(e) => setContaSearch(e.target.value)}
              placeholder="Customer ID ou nome..."
              disabled={!contas.length}
            />

            <label className="ga-label">Selecione uma conta</label>
            <select
              className="ga-select"
              value={customerId}
              onChange={(e) => onSelectConta(e.target.value)}
              disabled={!contas.length}
            >
              <option value="">-- Selecione --</option>
              {contasFiltradas.map((c) => (
                <option key={c.customer_id} value={c.customer_id}>
                  {c.customer_name ? `${c.customer_name} — ` : ""}
                  {c.customer_id}
                </option>
              ))}
            </select>

            <label className="ga-label">Customer Name (opcional)</label>
            <input
              className="ga-input"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Nome da conta (se quiser ajustar)"
              disabled={!customerId}
            />

            <button
              type="button"
              className="ga-btn ga-btn-primary"
              onClick={vincular}
              disabled={!usuarioId || !customerId}
            >
              Vincular ao usuário
            </button>

            <div className="ga-hint">
              Os campos fixos (MCC, client_id, client_secret, developer_token, refresh_token)
              ficam no backend e não aparecem aqui.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
